import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useSelector } from 'react-redux';
import { useFragment } from 'react-relay/hooks';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import {
  Autorenew as AutorenewIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Phone as PhoneIcon,
  RssFeed as RssFeedIcon,
  Smartphone as SmartphoneIcon,
  WhatsApp as WhatsAppIcon,
} from '@material-ui/icons';
import { graphql } from 'babel-plugin-relay/macro';
import { StatusIcon, Tooltip, Typography, TypographyProps, VoicePlayer } from 'embeds/components';
import { muiThemeLuma } from 'embeds/themes';
import { compact, flattenDeep, forEach, get, isEmpty, map } from 'lodash';
import moment from 'moment';
import { useGroupManyV2 } from 'ui-model';

import { selectCurrentMasterSettings } from '@lib/selectors';

import { TypographyMessageDetails_EmbedMessage$key } from './__generated__/TypographyMessageDetails_EmbedMessage.graphql';
import { normalizeRetriedChannels } from './utils/typographyMessageDetails';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'flex-end',
    },
    retryInfoWrapper: {
      display: 'flex',
      flexDirection: 'column',
      paddingBlock: `${unit / 2}px`,
      '& > p': {
        padding: 0,
        margin: 0,
      },
    },
    iconsAndDateWrapper: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1,
    },
    icon: {
      fontSize: 13,
      marginRight: unit / 2,
    },
    createdAt: {
      marginRight: unit / 2,
    },
    messageInfoVoicePlayer: {
      display: 'inline-block',
      float: 'left',
      padding: `0px ${unit / 2}px`,
    },
    metadata: {
      whiteSpace: 'pre-line',
    },
  });

const messages = defineMessages({
  facilityMetadata: {
    id: 'deprecated.fragments.TypographyMessageDetails.facilityMetadata',
    defaultMessage: 'Facility: {facility}',
  },
  groupsMetadata: {
    id: 'deprecated.fragments.TypographyMessageDetails.groupsMetadata',
    defaultMessage: 'Groups: {groups}',
  },
  broadcast: {
    id: 'deprecated.fragments.TypographyMessageDetails.broadcast',
    defaultMessage: 'Broadcast message',
  },
  retries: {
    id: 'deprecated.fragments.TypographyMessageDetails.retries',
    defaultMessage: 'Message was retried {label}:',
  },
  secure: {
    id: 'deprecated.fragments.TypographyMessageDetails.secure',
    defaultMessage: 'Secure Chat',
  },
  sentTo: {
    id: 'deprecated.fragments.TypographyMessageDetails.sentTo',
    defaultMessage: 'Sent to {to}',
  },
  voice: {
    id: 'deprecated.fragments.TypographyMessageDetails.voice',
    defaultMessage: 'Voice',
  },
  email: {
    id: 'deprecated.fragments.TypographyMessageDetails.email',
    defaultMessage: 'Email',
  },
  whatsapp: {
    id: 'deprecated.fragments.TypographyMessageDetails.whatsapp',
    defaultMessage: 'WhatsApp',
  },
  sms: {
    id: 'deprecated.fragments.TypographyMessageDetails.sms',
    defaultMessage: 'SMS',
  },
  andMessage: {
    id: 'deprecated.fragments.TypographyMessageDetails.and',
    defaultMessage: 'and',
  },
});

const fromListToLocaleString = (list: string[], andMessage: string, isToUseOxfordComma: boolean) => {
  const cloneList = [...list];
  if (cloneList.length === 1) return cloneList.join(', ');

  const lastElement = cloneList.pop() || '';
  cloneList.push(andMessage);
  cloneList.push(lastElement);
  const toString = cloneList.join(', ');
  return toString.replace(
    `${!isToUseOxfordComma ? ', ' : ''}${andMessage},`,
    `${!isToUseOxfordComma ? ' ' : ''}${andMessage}`,
  );
};

export interface TypographyMessageDetailsProps extends Omit<TypographyProps, 'classes'>, WithStyles<typeof styles> {
  fragmentRef: TypographyMessageDetails_EmbedMessage$key;
  retriedChannels: { name: string; reason: string }[];
}

const TypographyMessageDetails: React.FC<TypographyMessageDetailsProps> = ({
  classes,
  fragmentRef,
  retriedChannels,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const settings = useSelector(selectCurrentMasterSettings);
  const isToUseSendAndReceiverFilters = settings?.communication?.filterMessagesBySenderAndReceiver;
  const message = useFragment<TypographyMessageDetails_EmbedMessage$key>(
    graphql`
      fragment TypographyMessageDetails_EmbedMessage on Message {
        channel
        createdAt
        ref
        secure
        type
        visibility
        to
        facility {
          _id
          name
        }
        media {
          url
        }
        retry {
          count
        }
        notifyTo {
          channel
          to
        }

        # TODO Move fragment to StatusIcon_Message
        # visibility
        status
        externalStatus {
          reason
        }
      }
    `,
    fragmentRef,
  );

  const { data: groups } = useGroupManyV2(
    { facilities: message?.facility?._id },
    { enabled: Boolean(isToUseSendAndReceiverFilters && message?.facility) },
  );

  const groupsForMetadata = flattenDeep(groups?.pages).slice(0, 3);
  const unformattedGroupsMetadata = groupsForMetadata.map((item) => item.name);

  const localization = settings?.localization;
  const groupsNames = fromListToLocaleString(
    unformattedGroupsMetadata,
    formatMessage(messages.andMessage),
    localization?.includes('en'),
  );

  const icons = useMemo(() => {
    if (message.visibility === 'internal') {
      return [];
    }
    const array = [];
    if (message.ref === 'broadcast') {
      array.push({ Icon: RssFeedIcon, title: formatMessage(messages.broadcast) });
    }
    if (message.secure) {
      array.push({ Icon: LockIcon, title: formatMessage(messages.secure) });
    }
    let notifyToArray = compact(message.notifyTo);
    if (isEmpty(notifyToArray)) {
      notifyToArray = [{ channel: message.channel, to: message.to }];
    }
    forEach(notifyToArray, (notifyTo) => {
      let Icon;
      switch (get(notifyTo, 'channel')) {
        case 'voice':
          Icon = PhoneIcon;
          break;
        case 'email':
          Icon = EmailIcon;
          break;
        case 'whatsapp':
          Icon = WhatsAppIcon;
          break;
        case 'sms':
          Icon = SmartphoneIcon;
          break;
        default:
          break;
      }
      if (Icon) {
        const title = notifyTo.to
          ? formatMessage(messages.sentTo, { to: notifyTo.to })
          : formatMessage(messages[notifyTo.channel as 'voice' | 'email' | 'whatsapp' | 'sms']);
        const facilityMetadata = formatMessage(messages.facilityMetadata, { facility: message.facility?.name });
        const groupsMetadata = formatMessage(messages.groupsMetadata, { groups: groupsNames });
        array.push({
          Icon,
          title:
            isToUseSendAndReceiverFilters && message.facility ? (
              <span className={classes.metadata}>
                {title} {'\n'} {facilityMetadata} {'\n'} {groupsMetadata}
              </span>
            ) : (
              title
            ),
        });
      }
    });

    if (retriedChannels?.length > 0 && message.type === 'outbound' && !!message.retry?.count) {
      const normalizedRetriedChannels = normalizeRetriedChannels(retriedChannels);
      const count = message.retry.count;
      const label = count > 1 ? `${count} times` : `${count} time`;

      const title = (
        <>
          {formatMessage(messages.retries, { label })} <br />
          <div className={classes.retryInfoWrapper}>
            {normalizedRetriedChannels.map((item) => (
              <p key={`retry-text-${item.name}-${item.reason}`}>
                - {item.name}: {item.reason}
              </p>
            ))}
          </div>
        </>
      );
      array.push({ Icon: AutorenewIcon, title });
    }
    return array;
  }, [
    isToUseSendAndReceiverFilters,
    groupsNames,
    message,
    formatMessage,
    retriedChannels,
    classes.retryInfoWrapper,
    classes.metadata,
  ]);

  return (
    <Typography className={classes.root} {...rest}>
      {message.visibility !== 'internal' && message.channel === 'voice' && !!message.media && !!message.media.url && (
        <div className={classes.messageInfoVoicePlayer}>
          <VoicePlayer
            color={
              message.type !== 'outbound'
                ? muiThemeLuma.custom.palette.colorSupportMidnight
                : muiThemeLuma.palette.common.white
            }
            media={get(message, 'media.url')}
          />
        </div>
      )}
      <div className={classes.iconsAndDateWrapper}>
        {map(icons, ({ Icon, title }, idx) => (
          <Tooltip
            key={typeof title === 'string' ? title : `tooltip-icons-${idx + 1}-${title.props?.name}`}
            title={title}
            placement="top"
            hideArrow
          >
            <Icon className={classes.icon} />
          </Tooltip>
        ))}
        <Typography className={classes.createdAt} variant="inherit" color="inherit">
          {moment(message.createdAt as string).format('MMM DD, YYYY h:mm A')}
        </Typography>
        <StatusIcon isOutbound={message.type === 'outbound'} message={message} />
      </div>
    </Typography>
  );
};

export default withStyles(styles)(TypographyMessageDetails);
